import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Sidebar from '../Sidebar';
import Header from '../Header';
import PageHeader from '../ui/PageHeader';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { searchMedia, downloadMedia } from '../../lib/mediaLibrary';
import {
  Images,
  Image as ImageIcon,
  Video as VideoIcon,
  Search,
  Download,
  X,
  ExternalLink,
  Loader2,
  Clapperboard,
  AlertCircle,
  ArrowRight,
  LayoutGrid,
  RectangleHorizontal,
  RectangleVertical,
  Square,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

const SOURCES = [
  { id: 'pexels', label: 'Pexels' },
  { id: 'pixabay', label: 'Pixabay' },
];

const fmtDur = (sec) => {
  if (!sec && sec !== 0) return null;
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

// Tamanho do arquivo (KB/MB) quando o provedor informa (Pixabay informa;
// Pexels não expõe). Retorna null quando desconhecido (aí a UI omite).
const fmtBytes = (b) => {
  if (!b || b <= 0) return null;
  return b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`;
};

// dimensões · tamanho (filtra o que não existir)
const dimsAndSize = (item) => {
  const f = (item.files && item.files[0]) || {};
  const dims = item.width && item.height ? `${item.width}×${item.height}` : '';
  return [dims, fmtBytes(f.size)].filter(Boolean).join(' · ');
};

// Proporção da célula da grade conforme a orientação BUSCADA, pra retrato NÃO
// ser cortado em paisagem (era o que fazia parecer "travado em horizontal").
// 'all' = neutro 4:3.
const ratioFor = (orient) => ({
  horizontal: '16 / 10',
  vertical: '3 / 4',
  square: '1 / 1',
}[orient] || '4 / 3');

// Tier de resolução (4K/2K/1080p...) a partir das dimensões — vale p/ imagem E
// vídeo (imagem tem dimensão; só não tem o tamanho em MB).
const resLabel = (w, h) => {
  const m = Math.max(w || 0, h || 0);
  if (m >= 7680) return '8K';
  if (m >= 3840) return '4K';
  if (m >= 2560) return '2K';
  if (m >= 1920) return '1080p';
  if (m >= 1280) return '720p';
  if (m >= 854) return '480p';
  return m > 0 ? 'SD' : '';
};

const cardShell = {
  background: 'var(--dp-bg-card)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '14px',
  padding: '16px 18px',
};

// Pílula/aba padrão do sistema: SELECIONADA = degradê da marca
// (#0DAEFF→#3B82F6) + texto/ícone branco (mesmo seletor Aa/AA/aa do Text to
// Speech); não-selecionada = card-hover + texto secundário.
const chip = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 13px', borderRadius: '9999px', cursor: 'pointer',
  fontSize: '12.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
  border: `1px solid ${active ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
  background: active
    ? 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), var(--dp-blue-600, #0B8FDB))'
    : 'var(--dp-bg-card-hover)',
  color: active ? '#fff' : 'var(--dp-text-secondary)',
  transition: 'all 0.2s', whiteSpace: 'nowrap',
});

const MediaLibraryPage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('mediaLibrary.title'));
  const { sidebarWidth, isMobile } = useSidebar();

  const [type, setType] = useState('image');           // image | video
  const [query, setQuery] = useState('');
  const [orientation, setOrientation] = useState('horizontal');
  const [sources, setSources] = useState(['pexels', 'pixabay']);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [gridRatio, setGridRatio] = useState(ratioFor('horizontal'));
  const [masonry, setMasonry] = useState(false); // 'Todas' = proporção nativa por card

  const [modalItem, setModalItem] = useState(null);
  const [modalQ, setModalQ] = useState(0); // índice do arquivo/qualidade selecionado

  const reqRef = useRef(0);
  // Parâmetros da busca ATIVA (congelados ao clicar Buscar). Trocar
  // tab/orientação/fontes NÃO refaz a busca; o "carregar mais" usa estes.
  const activeSearchRef = useRef(null);

  const ORIENTATIONS = [
    { id: 'horizontal', label: t('mediaLibrary.orient_horizontal'), Icon: RectangleHorizontal },
    { id: 'vertical', label: t('mediaLibrary.orient_vertical'), Icon: RectangleVertical },
    { id: 'square', label: t('mediaLibrary.orient_square'), Icon: Square },
    { id: 'all', label: t('mediaLibrary.orient_all'), Icon: LayoutGrid },
  ];

  // Busca central. Recebe TODOS os parâmetros explícitos (sem closures stale).
  // append=false reinicia; append=true acrescenta a próxima página.
  const runSearch = useCallback(async ({ q, mtype, orient, srcs, pageNum, append }) => {
    if (!q || q.trim().length < 2) return;
    if (!srcs || srcs.length === 0) {
      toast.error(t('mediaLibrary.err_no_source'));
      return;
    }
    const token = ++reqRef.current;
    if (append) setLoadingMore(true); else { setLoading(true); setSearched(true); setGridRatio(ratioFor(orient)); setMasonry(orient === 'all'); }
    try {
      const res = await searchMedia({ type: mtype, q, orientation: orient, page: pageNum, sources: srcs });
      if (token !== reqRef.current) return; // resposta obsoleta — descarta
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
      setPage(res.page);
      setHasMore(res.hasMore);
      setNotConfigured(res.notConfigured);
    } catch (err) {
      if (token !== reqRef.current) return;
      const status = err?.response?.status;
      toast.error(status === 429 ? t('mediaLibrary.err_rate') : t('mediaLibrary.err_generic'));
    } finally {
      if (token === reqRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [t]);

  const submitSearch = () => {
    const q = query.trim();
    if (q.length < 2) { toast.error(t('mediaLibrary.err_short')); return; }
    if (!sources.length) { toast.error(t('mediaLibrary.err_no_source')); return; }
    // Congela os parâmetros DESTA busca: trocar tab/orientação/fontes depois NÃO
    // refaz a busca (o usuário clica em Buscar de novo); e o "carregar mais"
    // continua a busca ATIVA, não a seleção pendente.
    activeSearchRef.current = { q, mtype: type, orient: orientation, srcs: sources };
    runSearch({ q, mtype: type, orient: orientation, srcs: sources, pageNum: 1, append: false });
  };

  // Trocar tab / orientação / fontes apenas ATUALIZA a seleção — NÃO busca.
  // A busca só acontece ao clicar em "Buscar" (ou Enter no campo).
  const changeType = (newType) => { if (newType !== type) setType(newType); };
  const changeOrientation = (o) => { if (o !== orientation) setOrientation(o); };
  const toggleSource = (id) => {
    setSources((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const loadMore = () => {
    const a = activeSearchRef.current;
    if (!a || loading || loadingMore || !hasMore) return;
    runSearch({ ...a, pageNum: page + 1, append: true });
  };

  const openModal = (item) => { setModalItem(item); setModalQ(0); };
  const closeModal = () => setModalItem(null);

  const doDownload = (item, fileIdx = 0) => {
    const file = (item.files && item.files[fileIdx]) || null;
    const url = file?.url || item.src;
    const ext = file?.ext || (item.type === 'video' ? 'mp4' : 'jpg');
    downloadMedia(url, `${item.provider}-${String(item.id).split('-').pop()}.${ext}`);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1, minWidth: 0, transition: 'margin-left 0.3s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <Header />
        <main className="dp-main-content" style={{ marginTop: '60px', flex: 1, animation: 'dp-pageFadeIn 0.35s ease' }}>
          <PageHeader
            icon={<Images size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
            title={t('mediaLibrary.title')}
            description={t('mediaLibrary.subtitle')}
          />

          {/* ── Barra de controles ── */}
          <div className="dp-top-accent" style={{ ...cardShell, marginBottom: '16px' }}>
            {/* abas Imagens / Vídeos */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <button onClick={() => changeType('image')} style={chip(type === 'image')}>
                <ImageIcon size={14} /> {t('mediaLibrary.tab_images')}
              </button>
              <button onClick={() => changeType('video')} style={chip(type === 'video')}>
                <VideoIcon size={14} /> {t('mediaLibrary.tab_videos')}
              </button>
            </div>

            {/* input de busca */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
                  placeholder={t('mediaLibrary.search_placeholder')}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px 10px 38px', borderRadius: '10px',
                    background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
                    color: 'var(--dp-text-primary)', fontSize: '13.5px', fontFamily: 'var(--dp-font)',
                    outline: 'none',
                  }}
                />
              </div>
              <button className="dp-btn-cta dp-btn-cta--sm" onClick={submitSearch} disabled={loading}
                style={{ minWidth: '128px' }}>
                <Search size={14} />
                {loading ? t('mediaLibrary.searching') : t('mediaLibrary.search_button')}
                <ArrowRight size={14} />
              </button>
            </div>

            {/* fontes + orientação */}
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--dp-font)' }}>
                  {t('mediaLibrary.sources_label')}
                </span>
                {SOURCES.map((s) => (
                  <button key={s.id} onClick={() => toggleSource(s.id)} style={chip(sources.includes(s.id))}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--dp-font)' }}>
                  {t('mediaLibrary.orientation_label')}
                </span>
                {ORIENTATIONS.map((o) => (
                  <button key={o.id} onClick={() => changeOrientation(o.id)} style={chip(orientation === o.id)}>
                    <o.Icon size={13} /> {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Aviso: fontes não configuradas ── */}
          {notConfigured && searched && !loading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '14px 16px', marginBottom: '16px', borderRadius: '12px',
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
            }}>
              <AlertCircle size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
                {t('mediaLibrary.not_configured')}
              </span>
            </div>
          )}

          {/* ── Resultados ── */}
          {/* skeleton SÓ na primeira busca (sem itens). Em re-filtros os cards
              ficam visíveis (escurecidos) e só são trocados quando chega o novo
              resultado — evita o "piscar". */}
          {(loading && items.length === 0) ? (
            <div className={masonry ? 'ml-masonry' : 'ml-grid'} style={masonry ? undefined : { '--ml-ratio': gridRatio }}>
              {Array.from({ length: 15 }).map((_, i) => (
                <div key={i} className="dp-skeleton ml-card" style={{ borderRadius: '12px', aspectRatio: masonry ? ['3 / 4', '4 / 3', '1 / 1', '16 / 10'][i % 4] : undefined }} />
              ))}
            </div>
          ) : !searched ? (
            <EmptyState
              icon={<Images size={26} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
              title={t('mediaLibrary.empty_title')}
              desc={t('mediaLibrary.empty_desc')}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Search size={26} style={{ color: 'var(--dp-text-tertiary)' }} />}
              title={t('mediaLibrary.no_results_title')}
              desc={t('mediaLibrary.no_results_desc')}
            />
          ) : (
            <>
              <div style={{ position: 'relative' }}>
                {loading && (
                  <div style={{
                    position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', zIndex: 3,
                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                    padding: '6px 14px', borderRadius: '9999px',
                    background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                    boxShadow: '0 8px 24px -10px rgba(0,0,0,0.65)',
                    fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
                  }}>
                    <Loader2 size={13} className="animate-spin" /> {t('mediaLibrary.searching')}
                  </div>
                )}
                <div className={masonry ? 'ml-masonry' : 'ml-grid'} style={{ ...(masonry ? {} : { '--ml-ratio': gridRatio }), opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s', pointerEvents: loading ? 'none' : 'auto' }}>
                  {items.map((item) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      masonry={masonry}
                      onOpen={() => openModal(item)}
                      onDownload={() => doDownload(item, 0)}
                      downloadLabel={t('mediaLibrary.download')}
                    />
                  ))}
                </div>
              </div>
              {hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '22px' }}>
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '10px 22px', borderRadius: '10px',
                      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-medium)',
                      color: 'var(--dp-text-primary)', fontSize: '13px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)', cursor: loadingMore ? 'default' : 'pointer',
                      opacity: loadingMore ? 0.6 : 1, transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!loadingMore) { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.40)'; e.currentTarget.style.color = '#0DAEFF'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
                  >
                    {loadingMore && <Loader2 size={14} className="animate-spin" />}
                    {t('mediaLibrary.load_more')}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* ── Modal de preview ── */}
      {modalItem && (
        <PreviewModal
          item={modalItem}
          qIdx={modalQ}
          setQIdx={setModalQ}
          onClose={closeModal}
          onDownload={() => doDownload(modalItem, modalQ)}
          t={t}
        />
      )}

      <style>{`
        /* Grade FIXA (até 5 por linha) com células de proporção uniforme:
           o tamanho da célula é conhecido antes da imagem carregar (aspect-ratio),
           então NÃO há reflow/"piscar" — as imagens só fazem fade-in dentro delas. */
        .ml-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 14px;
        }
        .ml-grid .ml-card { aspect-ratio: var(--ml-ratio, 4 / 3); }
        /* 'Todas': masonry em colunas — cada card na proporção NATIVA (definida
           inline a partir das dimensões reais, então sem reflow/"piscar"). */
        .ml-masonry { columns: 5; column-gap: 14px; }
        .ml-masonry .ml-card { break-inside: avoid; margin-bottom: 14px; }
        @media (max-width: 1280px) { .ml-grid { grid-template-columns: repeat(4, 1fr); } .ml-masonry { columns: 4; } }
        @media (max-width: 980px)  { .ml-grid { grid-template-columns: repeat(3, 1fr); } .ml-masonry { columns: 3; } }
        @media (max-width: 640px)  { .ml-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; } .ml-masonry { columns: 2; column-gap: 10px; } }
      `}</style>
    </div>
  );
};

// ── Card de mídia (masonry) ──
const MediaCard = ({ item, onOpen, onDownload, downloadLabel, masonry }) => {
  const [hover, setHover] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dur = item.type === 'video' ? fmtDur(item.duration) : null;
  const tier = resLabel(item.width, item.height); // 4K/2K/1080p...
  // dimensões · tamanho — SEMPRE visível na faixa inferior do card.
  const metaText = dimsAndSize(item);
  // 'Todas' (masonry): proporção NATIVA do item (sem cortar). Fora disso a
  // célula uniforme da grade cuida do aspecto (via CSS var).
  const nativeAspect = masonry ? (item.width && item.height ? `${item.width} / ${item.height}` : '4 / 3') : undefined;
  return (
    <div
      className="ml-card"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', borderRadius: '12px', overflow: 'hidden', cursor: 'pointer',
        aspectRatio: nativeAspect,
        border: `1px solid ${hover ? 'rgba(13,174,255,0.4)' : 'var(--dp-border-subtle)'}`,
        background: 'var(--dp-bg-card)',
        boxShadow: hover ? '0 14px 36px -16px rgba(0,0,0,0.7)' : 'none',
        transition: 'border-color 0.18s, box-shadow 0.18s',
      }}
    >
      {/* placeholder shimmer até a imagem carregar — a célula já tem tamanho
          (aspect-ratio), então não há reflow/"piscar"; a imagem faz fade-in. */}
      {!loaded && <div className="dp-skeleton" style={{ position: 'absolute', inset: 0 }} />}
      <img
        src={item.thumb}
        alt={item.author || ''}
        loading="lazy"
        draggable={false}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', display: 'block',
          opacity: loaded ? 1 : 0,
          transform: hover ? 'scale(1.04)' : 'scale(1)',
          transition: 'opacity 0.35s ease, transform 0.3s ease',
        }}
      />

      {/* badge do provedor */}
      <span style={{
        position: 'absolute', top: 8, left: 8,
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px',
        padding: '3px 8px', borderRadius: '9999px', color: '#fff',
        background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.18)',
        backdropFilter: 'blur(4px)',
      }}>
        {item.provider_label}
      </span>

      {/* canto sup. direito: vídeo = DURAÇÃO (com ícone); imagem = tier de
          RESOLUÇÃO (4K/2K/1080p). Sem play central. */}
      {item.type === 'video' && dur && (
        <span style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontSize: '10.5px', fontWeight: 700, color: '#fff',
          padding: '3px 8px', borderRadius: '7px',
          background: 'rgba(0,0,0,0.68)', border: '1px solid rgba(255,255,255,0.16)',
          backdropFilter: 'blur(4px)',
        }}>
          <Clapperboard size={11} /> {dur}
        </span>
      )}
      {item.type === 'image' && tier && (
        <span style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          fontSize: '10px', fontWeight: 700, color: '#fff',
          padding: '3px 8px', borderRadius: '7px',
          background: 'rgba(0,0,0,0.68)', border: '1px solid rgba(255,255,255,0.16)',
          backdropFilter: 'blur(4px)',
        }}>
          {tier}
        </span>
      )}

      {/* faixa inferior SEMPRE visível: dimensões · tamanho (· duração).
          O botão Baixar aparece no hover. */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
        padding: '18px 10px 9px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.82), transparent)',
      }}>
        <span style={{ fontSize: '10.5px', fontWeight: 600, color: 'rgba(255,255,255,0.92)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {metaText || (item.author || '')}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          title={downloadLabel}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0,
            padding: '5px 11px', borderRadius: '8px', cursor: 'pointer',
            fontSize: '11px', fontWeight: 700, color: '#fff',
            background: 'rgba(13,174,255,0.95)', border: 'none', whiteSpace: 'nowrap',
            opacity: hover ? 1 : 0, pointerEvents: hover ? 'auto' : 'none', transition: 'opacity 0.18s',
          }}
        >
          <Download size={12} /> {downloadLabel}
        </button>
      </div>
    </div>
  );
};

// ── Estado vazio / inicial ──
const EmptyState = ({ icon, title, desc }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', gap: '8px', padding: '60px 20px',
  }}>
    <div style={{
      width: '60px', height: '60px', borderRadius: '16px', marginBottom: '6px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(13,174,255,0.07)', border: '1px solid rgba(13,174,255,0.12)',
    }}>
      {icon}
    </div>
    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>{title}</div>
    <div style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', maxWidth: '420px', lineHeight: 1.5 }}>{desc}</div>
  </div>
);

// ── Modal de preview ──
const PreviewModal = ({ item, qIdx, setQIdx, onClose, onDownload, t }) => {
  const files = item.files || [];
  const isVideo = item.type === 'video';
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, padding: '24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
        animation: 'dp-pageFadeIn 0.2s ease',
      }}
    >
      <button
        onClick={onClose}
        aria-label="Fechar"
        style={{
          position: 'fixed', top: 18, right: 18, width: 38, height: 38, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff',
        }}
      >
        <X size={18} />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto',
          background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
          borderRadius: '16px', padding: '16px',
        }}
      >
        {/* media */}
        <div style={{ borderRadius: '12px', overflow: 'hidden', background: '#000', marginBottom: '14px' }}>
          {isVideo ? (
            <video
              src={(files[qIdx] && files[qIdx].url) || item.src}
              poster={item.preview}
              controls
              autoPlay
              style={{ display: 'block', width: '100%', maxHeight: '60vh', background: '#000' }}
            />
          ) : (
            <img
              src={item.preview || item.src}
              alt={item.author || ''}
              style={{ display: 'block', width: '100%', maxHeight: '60vh', objectFit: 'contain', background: '#000' }}
            />
          )}
        </div>

        {/* meta + qualidade + ações */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
              {item.provider_label}
              {item.author ? ` · ${t('mediaLibrary.by', { author: item.author })}` : ''}
            </div>
            {item.source_page && (
              <a href={item.source_page} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '4px', fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500, #0DAEFF)', textDecoration: 'none' }}>
                <ExternalLink size={12} /> {t('mediaLibrary.source')}
              </a>
            )}
          </div>

          {/* seletor de qualidade (dropup padrão do sistema) */}
          {files.length > 1 && (
            <QualityDropup files={files} qIdx={qIdx} setQIdx={setQIdx} />
          )}

          <button className="dp-btn-cta dp-btn-cta--sm" onClick={onDownload}>
            <Download size={14} /> {t('mediaLibrary.download')} <ArrowRight size={14} />
          </button>
        </div>

        <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginTop: '12px', lineHeight: 1.5 }}>
          {t('mediaLibrary.attribution_note')}
        </div>
      </div>
    </div>
  );
};

// ── Dropup de qualidade (padrão do sistema): abre PRA CIMA, lista as
// qualidades com resolução + tamanho. Fecha ao clicar fora / Esc. ──
const QualityDropup = ({ files, qIdx, setQIdx }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  // Rótulo = tier de resolução (4K/2K/1080p...) p/ imagem E vídeo. Meta =
  // dimensões · tamanho (tamanho só existe em vídeo). Sem dims, cai no nome.
  const primary = (f) => ((f.width && f.height) ? resLabel(f.width, f.height) : (f.label || f.quality));
  const meta = (f) => {
    const parts = [];
    if (f.width && f.height) parts.push(`${f.width}×${f.height}`);
    const sz = fmtBytes(f.size);
    if (sz) parts.push(sz);
    return parts.join(' · ');
  };
  const cur = files[qIdx] || files[0] || {};

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
          minWidth: '180px', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer',
          fontSize: '12.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
          background: 'var(--dp-bg-card-hover)', border: '1px solid var(--dp-border-medium)',
          color: 'var(--dp-text-primary)', whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {primary(cur)}
          {meta(cur) ? <span style={{ color: 'var(--dp-text-tertiary)', fontWeight: 500 }}>{`  ·  ${meta(cur)}`}</span> : null}
        </span>
        {open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
      </button>

      {open && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: '6px',
          background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
          borderRadius: '10px', boxShadow: '0 14px 40px -12px rgba(0,0,0,0.75)',
          overflow: 'hidden', zIndex: 5, maxHeight: '260px', overflowY: 'auto',
        }}>
          {files.map((f, i) => (
            <button
              key={f.url + i}
              onClick={() => { setQIdx(i); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                width: '100%', textAlign: 'left', cursor: 'pointer', padding: '9px 12px',
                border: 'none', borderBottom: i < files.length - 1 ? '1px solid var(--dp-border-subtle)' : 'none',
                background: i === qIdx ? 'rgba(13,174,255,0.12)' : 'transparent',
                color: i === qIdx ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-text-secondary)',
                fontSize: '12.5px', fontWeight: i === qIdx ? 700 : 500, fontFamily: 'var(--dp-font)',
              }}
              onMouseEnter={(e) => { if (i !== qIdx) e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; }}
              onMouseLeave={(e) => { if (i !== qIdx) e.currentTarget.style.background = 'transparent'; }}
            >
              <span>{primary(f)}</span>
              <span style={{ fontSize: '11px', color: i === qIdx ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-text-tertiary)' }}>
                {meta(f)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibraryPage;
